<template>
  <div class="health-record-detail min-h-screen theme-bg">
    <!-- 顶部导航 -->
    <div class="fixed-header">
      <van-nav-bar 
        title="记录详情" 
        left-arrow 
        @click-left="navBack"
        class="theme-header"
        :border="false"
      />
    </div>
    
    <!-- 主要内容区域 -->
    <div class="pt-16 pb-20 px-4">
      <div v-if="loading" class="py-12 text-center">
        <van-loading type="spinner" color="#1989fa" />
        <p class="text-gray-500 mt-2">加载中...</p>
      </div>
      
      <div v-else-if="record">
        <!-- 记录标题卡片 -->
        <div class="bg-white rounded-2xl shadow-lg mb-4 overflow-hidden theme-card">
          <div class="p-4 flex justify-between items-center record-header" :class="getRecordTypeClass(record.healthRecordType)">
            <div class="flex items-center">
              <div class="w-12 h-12 rounded-full flex items-center justify-center bg-white bg-opacity-20 mr-3">
                <font-awesome-icon :icon="getRecordTypeIcon(record.healthRecordType)" class="text-white text-2xl" />
              </div>
              <div>
                <h2 class="font-bold text-white text-xl">{{ record.healthRecordName }}</h2>
                <p class="text-sm text-white text-opacity-80">{{ record.healthRecordTime }}</p>
              </div>
            </div>
            <van-tag plain round :color="getRecordTypeColor(record.healthRecordType)" size="large">
              {{ getRecordTypeName(record.healthRecordType) }}
            </van-tag>
          </div>
        </div>
        
        <!-- 基本信息卡片 -->
        <div class="bg-white rounded-2xl shadow-lg mb-4 p-4 card-gradient">
          <h3 class="text-gray-700 font-medium mb-3 flex items-center">
            <font-awesome-icon icon="info-circle" class="mr-2 theme-icon" />
            基本信息
          </h3>
          
          <div class="grid grid-cols-1 gap-3">
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">花费</span>
              <span class="text-gray-800 font-medium">{{ record.healthRecordCost ? `¥${record.healthRecordCost}` : '无' }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">执行机构</span>
              <span class="text-gray-800 font-medium">{{ record.hospitalName || '无' }}</span>
            </div>
            <!-- <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">操作人员</span>
              <span class="text-gray-800 font-medium">{{ record.veterinarian || '无' }}</span>
            </div> -->
          </div>
        </div>
        
        <!-- 详细信息卡片 -->
        <div class="bg-white rounded-2xl shadow-lg mb-4 p-4 card-gradient">
          <h3 class="text-gray-700 font-medium mb-3 flex items-center">
            <font-awesome-icon :icon="getRecordTypeIcon(record.healthRecordType)" class="mr-2" :class="getIconColorClass(record.healthRecordType)" />
            {{ getRecordTypeTitle(record.healthRecordType) }}
          </h3>
          
          <!-- 疫苗记录特有信息 -->
          <div v-if="record.healthRecordType === 1" class="grid grid-cols-1 gap-3">
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">疫苗名称</span>
              <span class="text-gray-800 font-medium">{{ record.vaccineName }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">品牌</span>
              <span class="text-gray-800 font-medium">{{ record.vaccineBrand || '无' }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">批号</span>
              <span class="text-gray-800 font-medium">{{ record.vaccineBatchNumber || '无' }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">下次接种</span>
              <span class="text-gray-800 font-medium">{{ record.vaccineNextTime }}</span>
            </div>
          </div>
          
          <!-- 驱虫记录特有信息 -->
          <div v-if="record.healthRecordType === 2" class="grid grid-cols-1 gap-3">
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">驱虫类型</span>
              <span class="text-gray-800 font-medium">{{ record.healthRecordType }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">药品名称</span>
              <span class="text-gray-800 font-medium">{{ record.drugName }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">剂量</span>
              <span class="text-gray-800 font-medium">{{ record.dose || '无' }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">下次驱虫</span>
              <span class="text-gray-800 font-medium">{{ record.dewormingNextTime }}</span>
            </div>
          </div>
          
          <!-- 体检记录特有信息 -->
          <div v-if="record.healthRecordType === 3" class="grid grid-cols-1 gap-3">
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">体重</span>
              <span class="text-gray-800 font-medium">{{ record.weight ? `${record.weight}kg` : '无' }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">体温</span>
              <span class="text-gray-800 font-medium">{{ record.temperature ? `${record.temperature}℃` : '无' }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">心率</span>
              <span class="text-gray-800 font-medium">{{ record.heartRate ? `${record.heartRate}次/分钟` : '无' }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-gray-500">检查结果</span>
              <span class="text-gray-800 font-medium">{{ record.inspectionResults || '无' }}</span>
            </div>
          </div>
          
          <!-- 生病记录特有信息 -->
          <div v-if="record.healthRecordType === 4" class="grid grid-cols-1 gap-3">
            <div class="flex flex-col py-2 border-b border-gray-100">
              <span class="text-gray-500 mb-1">症状</span>
              <span class="text-gray-800">{{ record.symptom }}</span>
            </div>
            <div class="flex flex-col py-2 border-b border-gray-100">
              <span class="text-gray-500 mb-1">诊断结果</span>
              <span class="text-gray-800">{{ record.diagnosticResults || '无' }}</span>
            </div>
            <div class="flex flex-col py-2 border-b border-gray-100">
              <span class="text-gray-500 mb-1">治疗方案</span>
              <span class="text-gray-800">{{ record.treatmentPlan || '无' }}</span>
            </div>
            <div class="flex flex-col py-2 border-b border-gray-100">
              <span class="text-gray-500 mb-1">用药情况</span>
              <span class="text-gray-800">{{ record.medicationSituation || '无' }}</span>
            </div>
          </div>
        </div>
        
        <!-- 详情描述卡片 -->
        <!-- <div v-if="record.details" class="bg-white rounded-2xl shadow-lg mb-4 p-4 card-gradient">
          <h3 class="text-gray-700 font-medium mb-3 flex items-center">
            <font-awesome-icon icon="clipboard" class="mr-2 theme-icon" />
            详情描述
          </h3>
          <p class="text-gray-700 whitespace-pre-line">{{ record.details }}</p>
        </div> -->
        
        <!-- 附件卡片 -->
        <div v-if="record.diagnosticImages && record.diagnosticImages.length > 0" class="bg-white rounded-2xl shadow-lg mb-4 p-4 card-gradient">
          <h3 class="text-gray-700 font-medium mb-3 flex items-center">
            <font-awesome-icon icon="image" class="mr-2 theme-icon" />
            附件 ({{ record.diagnosticImages.split(',').length }})
          </h3>
          
          <div class="grid grid-cols-2 gap-3">
            <div 
              v-for="(attachment, index) in record.diagnosticImages.split(',')" 
              :key="index" 
              class="rounded-lg overflow-hidden shadow-sm cursor-pointer bg-gradient-to-br from-blue-50 to-blue-100"
              @click="previewImage(record.diagnosticImages,attachment)"
            >
              <img :src="attachment" class="w-full h-40 object-cover" />
              <!-- <p class="text-xs p-2 text-center text-gray-500">{{ attachment.name }}</p> -->
            </div>
          </div>
        </div>
        <!-- 附件卡片 -->
        <div v-if="record.inspectionImages && record.inspectionImages.length > 0" class="bg-white rounded-2xl shadow-lg mb-4 p-4 card-gradient">
          <h3 class="text-gray-700 font-medium mb-3 flex items-center">
            <font-awesome-icon icon="image" class="mr-2 theme-icon" />
            附件 ({{ record.inspectionImages.split(',').length }})
          </h3>
          
          <div class="grid grid-cols-2 gap-3">
            <div 
              v-for="(attachment, index) in record.inspectionImages.split(',')" 
              :key="index" 
              class="rounded-lg overflow-hidden shadow-sm cursor-pointer bg-gradient-to-br from-blue-50 to-blue-100"
              @click="previewImage(record.inspectionImages,attachment)"
            >
              <img :src="attachment" class="w-full h-40 object-cover" />
              <!-- <p class="text-xs p-2 text-center text-gray-500">{{ attachment.name }}</p> -->
            </div>
          </div>
        </div>
        
        <!-- 操作按钮 -->
        <div class="fixed bottom-6 left-0 right-0 px-4">
          <div class="flex space-x-3">
            <van-button 
              type="primary" 
              block 
              @click="editRecord"
              class="flex-1 theme-btn-primary"
              round
            >
              <font-awesome-icon icon="edit" class="mr-1" />
              编辑记录
            </van-button>
            <van-button 
              type="danger" 
              plain
              @click="deleteRecord"
              class="flex-1 theme-btn-danger"
              round
            >
              <font-awesome-icon icon="trash-alt" class="mr-1" />
              删除记录
            </van-button>
          </div>
        </div>
      </div>
      <div v-else class="py-12 text-center">
        <font-awesome-icon icon="exclamation-triangle" class="text-4xl text-yellow-400" />
        <p class="text-gray-500 mt-4">记录不存在或已被删除</p>
        <van-button plain type="primary" size="small" class="mt-4 theme-btn-outline" @click="$router.push('/health-record-list')">
          返回列表
        </van-button>
      </div>
    </div>
    
    <!-- 图片预览 -->
    <van-image-preview 
      v-model="showImagePreview"
      :images="previewImages"
      :start-position="previewIndex"
    />
    
    <!-- 确认删除弹窗 -->
    <van-dialog
      v-model="showDeleteConfirm"
      title="确认删除"
      message="删除后无法恢复，确定要删除这条记录吗？"
      show-cancel-button
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
/**
 * 宠物健康记录详情页面
 * 查看记录的详细信息
 * @component
 */
import { RPC } from '@/api/rpc'
import { mapActions  } from 'vuex'
export default {
  name: 'HealthRecordDetail',
  data() {
    return {
      /**
       * 记录ID
       * @type {String}
       */
      recordId: '',
      /**
       * 记录详情
       * @type {Object}
       */
      record: null,
      /**
       * 是否正在加载
       * @type {Boolean}
       */
      loading: true,
      /**
       * 图片预览是否显示
       * @type {Boolean}
       */
      showImagePreview: false,
      /**
       * 预览图片列表
       * @type {Array}
       */
      previewImages: [],
      /**
       * 当前预览图片索引
       * @type {Number}
       */
      previewIndex: 0,
      /**
       * 删除确认对话框是否显示
       * @type {Boolean}
       */
      showDeleteConfirm: false
    }
  },
  created() {
    // 获取路由参数中的记录ID
    this.recordId = this.$route.query.id || '';
    // 加载记录详情
    this.fetchRecordDetail();
  },
  methods: {
    ...mapActions(['setBackMachineId']),
    navBack() {
      this.$router.back()
      this.setBackMachineId(this.record.machineId)
    },
    /**
     * 获取记录类型图标
     * @param {String} type - 记录类型
     * @returns {String} 对应的图标名称
     */
    getRecordTypeIcon(type) {
      const icons = {
        1: 'syringe',
        2: 'bug',
        3: 'stethoscope',
        4: 'briefcase-medical'
      };
      return icons[type] || 'clipboard';
    },
    /**
     * 获取记录类型名称
     * @param {String} type - 记录类型
     * @returns {String} 中文名称
     */
    getRecordTypeName(type) {
      const names = {
        1: '疫苗',
        2: '驱虫',
        3: '体检',
        4: '生病'
      };
      return names[type] || '未知';
    },
    /**
     * 获取记录类型颜色
     * @param {String} type - 记录类型
     * @returns {String} 颜色代码
     */
    getRecordTypeColor(type) {
      const colors = {
        1: '#4facfe',
        2: '#2dd4bf',
        3: '#f6d365',
        4: '#ff7d89'
      };
      return colors[type] || '#4facfe';
    },
    /**
     * 获取记录类型样式类
     * @param {String} type - 记录类型
     * @returns {String} CSS类名
     */
    getRecordTypeClass(type) {
      const classes = {
        1: 'bg-gradient-to-r from-blue-400 to-blue-500',
        2: 'bg-gradient-to-r from-teal-400 to-teal-500',
        3: 'bg-gradient-to-r from-yellow-400 to-yellow-500',
        4: 'bg-gradient-to-r from-red-400 to-red-500'
      };
      return classes[type] || 'bg-gradient-to-r from-blue-400 to-blue-500';
    },
    /**
     * 获取图标颜色类
     * @param {String} type - 记录类型
     * @returns {String} CSS类名
     */
    getIconColorClass(type) {
      const classes = {
        1: 'text-blue-500',
        2: 'text-teal-500',
        3: 'text-yellow-500',
        4: 'text-red-500'
      };
      return classes[type] || 'text-blue-500';
    },

    /**
     * 获取记录类型标题
     * @param {String} type - 记录类型
     * @returns {String} 标题文本
     */
    getRecordTypeTitle(type) {
      const titles = {
        1: '疫苗信息',
        2: '驱虫信息',
        3: '体检信息',
        4: '病症信息'
      };
      return titles[type] || '记录信息';
    },
    /**
     * 获取记录详情
     */
    async fetchRecordDetail() {
      this.loading = true;
      
      try {
        RPC.healthRecordInfo(this.recordId).then(res=>{
          this.record = res.data
        })
      } catch (error) {
        console.error('获取记录详情失败:', error);
        this.$toast.fail('获取详情失败，请重试');
        this.record = null;
      } finally {
        this.loading = false;
      }
    },
    /**
     * 预览图片
     * @param {String} url - 图片URL
     */
    previewImage(ary,url) {
      this.previewImages = ary.split(',');
      this.previewIndex = this.previewImages.indexOf(url);
      this.showImagePreview = true;
    },
    /**
     * 编辑记录
     */
    editRecord() {
      this.$router.push({
        path: '/health-record',
        query: { 
          id: this.recordId,
        }
      });
    },
    /**
     * 删除记录
     */
    deleteRecord() {
      this.showDeleteConfirm = true;
    },
    /**
     * 确认删除记录
     */
    async confirmDelete() {
      try {
        RPC.healthRecordDelete({healthRecordId:this.recordId}).then(res=>{
          this.$toast.success('删除成功');
          this.$router.replace('/health-record-list');
        })
      } catch (error) {
        console.error('删除记录失败:', error);
        this.$toast.fail('删除失败，请重试');
      }
    }
  }
}
</script>

<style scoped>
.theme-header {
  background-image: var(--gradient-header);
  width: 100%;
  z-index: 50;
}

.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}

:deep(.van-nav-bar .van-icon) {
  color: var(--text-primary);
}

:deep(.van-nav-bar__title) {
  color: var(--text-primary);
  font-weight: 600;
}

:deep(.van-button--primary) {
  background: linear-gradient(to right, var(--primary-color), var(--primary-light));
  border: none;
}

.theme-btn-primary {
  background: linear-gradient(to right, var(--primary-color), var(--primary-light)) !important;
  border: none !important;
  color: white !important;
  font-weight: 600;
}

.theme-btn-outline {
  border-color: var(--primary-color) !important;
  color: var(--primary-color) !important;
  font-weight: 500;
}

.theme-btn-danger {
  border-color: #ee0a24 !important;
  color: #ee0a24 !important;
  font-weight: 500;
}

.theme-icon {
  color: var(--primary-color);
}

.record-header.bg-gradient-to-r.from-blue-400.to-blue-500 {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-light) 100%);
}

.record-header.bg-gradient-to-r.from-teal-400.to-teal-500 {
  background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%);
}

.record-header.bg-gradient-to-r.from-yellow-400.to-yellow-500 {
  background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
}

.record-header.bg-gradient-to-r.from-red-400.to-red-500 {
  background: linear-gradient(135deg, #ff7d89 0%, #ff7d89 100%);
}

.card-gradient {
  background: linear-gradient(to bottom right, #f9f9f9, #ffffff);
  border-left: 3px solid var(--primary-color);
}
</style> 